// dependencies
import React, { useState } from 'react';
import * as RDS from 'ouisys-engine/dist/common-types/RemoteDataState';
import { defineMessages, Input, InputType, Select, SelectType, Button, Link } from 'ouisys-component-library';
import { IUserDetails } from 'ouisys-engine/dist/reducers/creditCardFlow/CreditCardTypes';
import { MSISDNEntryFailure, MSISDNEntrySuccess } from 'ouisys-engine/dist/reducers/pinFlow/PinTypes';

// local
import { injectIntl, Translate } from '../../localization/index';
import Loader from '../Loader/index';
import './UserDetailsEntryStep.scss';

// error messages messages which might come from backend
const defaultMessages = defineMessages({
  placeholderName: {
    id: 'placeholderName',
    defaultMessage: 'Name'
  },
  placeholderEmail: {
    id: 'placeholderEmail',
    defaultMessage: 'Email'
  },
  placeholderAddress: {
    id: 'placeholderAddress',
    defaultMessage: 'Address'
  },
  placeholderPostcode: {
    id: 'placeholderPostcode',
    defaultMessage: 'Postcode'
  },
  placeholderCity: {
    id: 'placeholderCity',
    defaultMessage: 'City'
  },
  placeholderCountry: {
    id: 'placeholderCountry',
    defaultMessage: 'City'
  },
  invalidUserDetails: {
    id: 'invalidUserDetails',
    defaultMessage: 'User details are invalid.'
  },
  fieldRequired: {
    id: 'fieldRequired',
    defaultMessage: 'This field is required.'
  }
});

interface UserDetailsEntryStepProps {
  rds: RDS.RemoteDataState<MSISDNEntryFailure, MSISDNEntrySuccess>;
  onEnd: (userDetails: IUserDetails) => void;
  intl: any;
}

const UserDetailsEntryStep = ({ rds, onEnd, intl }: UserDetailsEntryStepProps) => {
  const [formData, setFormData] = useState({
    name: { value: '', valid: true },
    email: { value: '', valid: true },
    address: { value: '', valid: true },
    postcode: { value: '', valid: true },
    city: { value: '', valid: true },
    country: { value: '', valid: true },
  });

  const validateField = (event) => {
    event.persist();
    const name = event.target.name;
    const value = event.target.value;
    const valid = value.length > 0;
    setFormData({
      ...formData,
      [name]: { value, valid }
    });
    return valid;
  }

  return (
    <div className="subscriptionArea UserDetailsEntryStep">
      <form
        onSubmit={(ev) => {
          ev.preventDefault();
          onEnd({
            name: formData.name.value,
            email: formData.email.value,
            address: formData.address.value,
            postcode: formData.postcode.value,
            city: formData.city.value,
            country: formData.country.value,
          });
        }}
      >

        <Input
          required
          name="name"
          // label="Name"
          placeholder={intl.formatMessage(defaultMessages.placeholderName)}
          value={formData.name.value}
          onBlur={validateField}
          onChange={validateField}
          type={InputType.Text}
          error={!formData.name.valid && intl.formatMessage(defaultMessages.fieldRequired)}
        />
        <Input
          required
          name="email"
          // label="Email"
          placeholder={intl.formatMessage(defaultMessages.placeholderEmail)}
          value={formData.email.value}
          onBlur={validateField}
          onChange={validateField}
          type={InputType.Email}
          error={!formData.email.valid && intl.formatMessage(defaultMessages.fieldRequired)}
        />
        <Input
          required
          name="address"
          // label="Address"
          placeholder={intl.formatMessage(defaultMessages.placeholderAddress)}
          value={formData.address.value}
          onBlur={validateField}
          onChange={validateField}
          type={InputType.Text}
          error={!formData.address.valid && intl.formatMessage(defaultMessages.fieldRequired)}
        />
        <Input
          required
          name="postcode"
          // label="Postcode"
          placeholder={intl.formatMessage(defaultMessages.placeholderPostcode)}
          value={formData.postcode.value}
          onBlur={validateField}
          onChange={validateField}
          type={InputType.Text}
          error={!formData.postcode.valid && intl.formatMessage(defaultMessages.fieldRequired)}
        />
        <Input
          required
          name="city"
          // label="City"
          placeholder={intl.formatMessage(defaultMessages.placeholderCity)}
          value={formData.city.value}
          onBlur={validateField}
          onChange={validateField}
          type={InputType.Text}
          error={!formData.city.valid && intl.formatMessage(defaultMessages.fieldRequired)}
        />
        <Select
          required
          name="country"
          // label="Country"
          placeholder={intl.formatMessage(defaultMessages.placeholderCountry)}
          value={formData.country.value}
          onBlur={validateField}
          onChange={validateField}
          type={SelectType.Country}
          error={!formData.country.valid && intl.formatMessage(defaultMessages.fieldRequired)}
        />

        <Button id="user-details-submit-button" type="submit" className="btn" isDcbp>
          <Translate id="subscribe" defaultMessage="Subscribe Now" />
        </Button>

        {RDS.WhenLoading(null, () => (
          <div className="loading-wrapper">
            <Loader />
            <div>
              <Translate id="loadingLabel" defaultMessage="Please wait..." />
            </div>
          </div>
        ))(rds)}

        {RDS.WhenFailure(null, (err: MSISDNEntryFailure) => {
          return (
            <div className="errorMsg">
              <Translate id={err.errorType} />
              <br />
              {err.errorType == 'AlreadySubscribed' && (
                <Link target="_blank" href={err.productUrl}>
                  <Translate id="goToProductLabel" defaultMessage="Go to Product" />
                </Link>
              )}
            </div>
          );
        })(rds)}

      </form>
    </div>
  );
};

export default injectIntl(UserDetailsEntryStep);
